/**
 * VersionHistoryPanel Component
 *
 * Side panel showing version history with ability to:
 * - View list of all versions
 * - See version metadata (type, date, user, notes)
 * - Restore to a previous version
 */

import { format, formatDistanceToNow } from 'date-fns';
import { AlertCircle, FileText, History, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useRestoreVersion, useVersions } from '../../hooks';
import type { CDVersion, VersionType } from '../../types';

export interface VersionHistoryPanelProps {
  /**
   * Whether the panel is open
   */
  open: boolean;

  /**
   * Callback when the panel should close
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Transaction ID to show versions for
   */
  transactionId: string;

  /**
   * Current version number (to highlight)
   */
  currentVersionNumber?: number;

  /**
   * Callback after successful restore
   */
  onVersionRestored?: (versionNumber: number) => void;
}

const versionTypeBadgeVariants: Record<VersionType, 'default' | 'secondary' | 'outline'> = {
  draft: 'outline',
  reconciled: 'secondary',
  approved: 'default',
  final: 'default',
};

const versionTypeLabels: Record<VersionType, string> = {
  draft: 'Draft',
  reconciled: 'Reconciled',
  approved: 'Approved',
  final: 'Final',
};

export function VersionHistoryPanel({
  open,
  onOpenChange,
  transactionId,
  currentVersionNumber,
  onVersionRestored,
}: VersionHistoryPanelProps) {
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<CDVersion | null>(null);

  const { data: versions = [], isLoading, error } = useVersions(transactionId, { enabled: open });
  const restoreMutation = useRestoreVersion();

  const handleRestoreClick = (version: CDVersion) => {
    setSelectedVersion(version);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedVersion) return;

    try {
      await restoreMutation.mutateAsync({
        transactionId,
        versionId: selectedVersion.id,
        request: {
          notes: `Restored from version ${selectedVersion.versionNumber}`,
        },
      });

      setRestoreDialogOpen(false);
      setSelectedVersion(null);

      if (onVersionRestored) {
        onVersionRestored(selectedVersion.versionNumber);
      }
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </SheetTitle>
            <SheetDescription>
              View and restore previous versions of this Closing Disclosure.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="h-8 w-8 text-destructive mb-2" />
                <p className="text-sm text-muted-foreground">Failed to load version history</p>
              </div>
            ) : versions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No versions created yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Versions are created when you save changes in reconciling, approved, or finalized
                  status.
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="space-y-4 pr-4">
                  {versions.map((version: CDVersion, index: number) => (
                    <VersionHistoryItem
                      key={version.id}
                      version={version}
                      isCurrent={version.versionNumber === currentVersionNumber}
                      isLatest={index === 0}
                      onRestore={handleRestoreClick}
                      isRestoring={restoreMutation.isPending && selectedVersion?.id === version.id}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Version {selectedVersion?.versionNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new version based on the selected version&apos;s snapshot. Your
              current draft will be overwritten with the restored data. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreConfirm}
              disabled={restoreMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {restoreMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore Version
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface VersionHistoryItemProps {
  version: CDVersion;
  isCurrent: boolean;
  isLatest: boolean;
  onRestore: (version: CDVersion) => void;
  isRestoring: boolean;
}

function VersionHistoryItem({
  version,
  isCurrent,
  isLatest,
  onRestore,
  isRestoring,
}: VersionHistoryItemProps) {
  return (
    <div
      className={`rounded-lg border p-4 ${isCurrent ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'} transition-colors`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">Version {version.versionNumber}</span>
            <Badge variant={versionTypeBadgeVariants[version.versionType]}>
              {versionTypeLabels[version.versionType]}
            </Badge>
            {isCurrent && (
              <Badge variant="outline" className="text-xs">
                Current
              </Badge>
            )}
            {isLatest && !isCurrent && (
              <Badge variant="outline" className="text-xs">
                Latest
              </Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
          </p>

          <p className="text-xs text-muted-foreground">
            {format(new Date(version.createdAt), 'MMM d, yyyy h:mm a')}
          </p>

          {version.notes && (
            <>
              <Separator className="my-2" />
              <p className="text-sm">{version.notes}</p>
            </>
          )}

          {version.sourceDocumentId && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Created from uploaded document
            </p>
          )}
        </div>

        {!isCurrent && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRestore(version)}
            disabled={isRestoring}
          >
            {isRestoring ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
